import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle, FaStar } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest } from '../../services/api/apiService';
import { useLang, detectLang } from '../../i18n/LanguageContext';
import { LANGS } from '../../i18n/translations';

const PERKS = [
  { text: 'Connect 5+ ad platforms with one click', color: '#221C51', bg: '#EEF2FF' },
  { text: 'AI generates platform-optimised ad copy', color: '#1877f2', bg: '#EEF4FF' },
  { text: 'Automation rules that run 24/7', color: '#D97706', bg: '#FFFBEB' },
  { text: 'Live analytics across every campaign', color: '#059669', bg: '#ECFDF5' },
  { text: 'Free to start — no credit card needed', color: '#7C3AED', bg: '#F5F3FF' },
];

const AVATARS = [
  { initials: 'ED', color: '#221C51', bg: '#EEF2FF' },
  { initials: 'MR', color: '#1877f2', bg: '#EEF4FF' },
  { initials: 'AM', color: '#059669', bg: '#ECFDF5' },
  { initials: 'JK', color: '#D97706', bg: '#FFFBEB' },
];

const SignUp = () => {
  const { t, setLang } = useLang();
  const [username, setUsername]         = useState('');
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  // Langue de l'utilisateur : pré-détectée depuis la locale du navigateur
  // (équivalent du "choix par IP", sans appel externe ni géoloc), modifiable
  // dans le formulaire, persistée sur le compte à l'inscription.
  const [language, setLanguage]         = useState(detectLang());

  // Porte d'entrée d'inscription : ?source=shopify (posé par /shopify) — ou,
  // à défaut, la valeur déjà mémorisée si l'utilisateur a d'abord visité la
  // page marchand. Envoyée au serveur qui la fige dans users.signup_source :
  // c'est elle qui choisit le parcours d'onboarding, définitivement.
  const signupSource = React.useMemo(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('source');
    if (fromUrl) { try { localStorage.setItem('oba_source', fromUrl); } catch { /* stockage indispo */ } return fromUrl; }
    try { return localStorage.getItem('oba_source') || undefined; } catch { return undefined; }
  }, []);
  const isMerchant = signupSource === 'shopify';
  const [errors, setErrors]             = useState({ username: false, email: false, password: false });
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const chooseLanguage = (code) => { setLanguage(code); setLang(code); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { username: !username, email: !email, password: !password };
    setErrors(newErrors);
    if (newErrors.username || newErrors.email || newErrors.password) {
      setErrorMessage('All fields are required.');
      return;
    }
    try {
      await postRequest('/api/auth/signup', { username, email, password, language, signupSource });
      navigate('/login');
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Sign-up failed. Please try again.');
    }
  };

  const handleGoogleSignUp = async (response) => {
    try {
      await postRequest('/api/auth/google-signup', { token: response.credential, language, signupSource });
      navigate('/login');
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Google sign-up failed. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <style>{`
        .auth-input {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-size: 14px; color: #0F0B2D; background: #F9FAFB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .auth-input::placeholder { color: #9CA3AF; }
        .auth-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .auth-input.error { border-color: #EF4444; background: #fff; }
        .auth-submit-btn {
          width: 100%; padding: 13px; border-radius: 10px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .auth-submit-btn:hover { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        @media (max-width: 900px) { .auth-left-panel { display: none !important; } }
      `}</style>

      {/* ── LEFT BRAND PANEL ── */}
      <div className="auth-left-panel" style={{
        width: '50%',
        background: 'linear-gradient(145deg, #F0F4FF 0%, #EEF2FF 50%, #F5F3FF 100%)',
        borderRight: '1px solid #E0E4F0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '48px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -120, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,28,81,0.06) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -100, left: -80, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link to="/">
            <img src={logo} alt="OneBotAds" style={{ height: 32, marginBottom: 40, display: 'block' }} />
          </Link>

          <h2 style={{ fontSize: 'clamp(22px,2.4vw,30px)', fontWeight: 900, color: '#0F0B2D', lineHeight: 1.25, marginBottom: 10, letterSpacing: '-0.02em' }}>
            Join 200+ marketing teams<br />scaling with OneBotAds.
          </h2>
          <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.7, marginBottom: 32, maxWidth: 360 }}>
            One platform to create, launch, and automate ads across every channel. Free to start.
          </p>

          {/* Perks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 36 }}>
            {PERKS.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: p.bg, border: `1px solid ${p.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 9, color: p.color }} />
                </div>
                <span style={{ fontSize: 13.5, color: '#374151', fontWeight: 500 }}>{p.text}</span>
              </div>
            ))}
          </div>

          {/* Social proof bar */}
          <div style={{ padding: '16px 18px', background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex' }}>
              {AVATARS.map((a, i) => (
                <div key={i} style={{ width: 30, height: 30, borderRadius: '50%', background: a.bg, border: `2px solid #fff`, marginLeft: i === 0 ? 0 : -8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: a.color, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  {a.initials}
                </div>
              ))}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 1, marginBottom: 3 }}>
                {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 9, color: '#F59E0B' }} />)}
              </div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                <strong style={{ color: '#0F0B2D' }}>200+ teams</strong> · avg. rating 4.9/5
              </div>
            </div>
          </div>

          {/* Platform badges */}
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, whiteSpace: 'nowrap' }}>Works with</span>
            {[
              { icon: <FaTiktok />, color: '#010101', bg: '#F3F4F6' },
              { icon: <FaFacebookF />, color: '#1877f2', bg: '#EEF4FF' },
              { icon: <FaLinkedinIn />, color: '#0a66c2', bg: '#EFF6FF' },
              { icon: <FaGoogle />, color: '#4285f4', bg: '#EFF6FF' },
              { icon: '👻', color: '#8B7500', bg: '#FEFCE8' },
            ].map((p, i) => (
              <div key={i} style={{ width: 30, height: 30, borderRadius: 8, background: p.bg, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: p.color }}>
                {p.icon}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div style={{ flex: 1, background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px' }}>
        <style>{`@media(max-width:900px){.mobile-logo-auth{display:flex!important;}}`}</style>
        <Link to="/" style={{ display: 'none', marginBottom: 28 }} className="mobile-logo-auth">
          <img src={logo} alt="OneBotAds" style={{ height: 32 }} />
        </Link>

        <div style={{ width: '100%', maxWidth: 380 }}>
          {/* Contexte marchand : l'utilisateur vient de /shopify. On le dit
              explicitement — l'inscription enchaîne sur la connexion boutique. */}
          {isMerchant && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20,
              padding: '12px 14px', borderRadius: 12,
              background: '#F0FDF4', border: '1px solid #A7F3D0',
            }}>
              <span style={{ fontSize: 18, lineHeight: 1.2 }}>🛍️</span>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#065F46' }}>
                  Inscription marchand Shopify
                </div>
                <div style={{ fontSize: 12.5, color: '#047857', lineHeight: 1.5, marginTop: 2 }}>
                  Juste après la création du compte, vous connectez votre boutique
                  et importez votre catalogue.
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F0B2D', marginBottom: 6, letterSpacing: '-0.02em' }}>Create your account</h1>
            <p style={{ fontSize: 14, color: '#6B7280' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#221C51', fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
            </p>
          </div>

          {errorMessage && (
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, marginBottom: 20 }}>
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Username</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="text" value={username} onChange={e => { setUsername(e.target.value); setErrors(v => ({ ...v, username: false })); setErrorMessage(''); }} placeholder="Your username" className={`auth-input${errors.username ? ' error' : ''}`} />
              </div>
              {errors.username && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Username is required</p>}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Email address</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(v => ({ ...v, email: false })); setErrorMessage(''); }} placeholder="you@example.com" className={`auth-input${errors.email ? ' error' : ''}`} />
              </div>
              {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Email is required</p>}
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(v => ({ ...v, password: false })); setErrorMessage(''); }} placeholder="Min. 8 characters" className={`auth-input${errors.password ? ' error' : ''}`} />
              </div>
              {errors.password && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Password is required</p>}
            </div>

            {/* Langue — pré-détectée depuis le navigateur, modifiable, persistée sur le compte */}
            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                {t('Choisissez votre langue')}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => chooseLanguage(l.code)}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      border: language === l.code ? '2px solid #221C51' : '1.5px solid #E5E7EB',
                      background: language === l.code ? '#EEF2FF' : '#F9FAFB',
                      color: '#0F0B2D',
                    }}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="auth-submit-btn">
              Create account <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <GoogleLogin onSuccess={handleGoogleSignUp} onError={() => setErrorMessage('Google sign-up failed. Please try again.')} />
          </div>

          <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.6 }}>
            By signing up you agree to our{' '}
            <span style={{ color: '#221C51', cursor: 'pointer', fontWeight: 500 }}>Terms</span>{' '}
            and <span style={{ color: '#221C51', cursor: 'pointer', fontWeight: 500 }}>Privacy Policy</span>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
